import React from 'react'

import { Join } from '../../lib/utils'
import { H3 } from '../typography/headings'
import {
  Card,
  CardBody,
  CardTitle,
  type CardGlassVariant,
  type CardProps,
} from './card'

const accentTitleClass =
  'mb-3 text-xl leading-snug font-light text-accent sm:text-2xl'

export interface GlassCardProps extends Omit<CardProps, 'surface'> {
  title?: React.ReactNode
  titleClassName?: string
  /**
   * `accent` — speclogician-style blue H3 title.
   * `default` — token `CardTitle` (IPL marketing grids).
   */
  titleStyle?: 'accent' | 'default'
}

/**
 * Frosted glass marketing card — composes `Card surface="glass"` with optional
 * title. Use `GlassCardBody` for description text (speclogician parity).
 */
export function GlassCard({
  title,
  titleClassName,
  titleStyle = 'accent',
  children,
  className,
  glassVariant = 'accent',
  radius = 'xl',
  padding = 'lg',
  tone = 'default',
  ...cardProps
}: GlassCardProps) {
  return (
    <Card
      surface="glass"
      glassVariant={glassVariant}
      tone={tone}
      radius={radius}
      padding={padding}
      className={className}
      {...cardProps}
    >
      {title &&
        (titleStyle === 'accent' ? (
          <H3 size="sm" className={Join(accentTitleClass, titleClassName)}>
            {title}
          </H3>
        ) : (
          <CardTitle className={titleClassName}>{title}</CardTitle>
        ))}
      {children}
    </Card>
  )
}

/** Body copy for `GlassCard` — thin wrapper over `CardBody`. */
export function GlassCardBody({
  children,
  className,
}: {
  children: React.ReactNode
  className?: string
}) {
  return <CardBody className={className}>{children}</CardBody>
}

export type { CardGlassVariant }
